import { Component, input, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { Button } from 'primeng/button';
import { ContentDocument } from '@/lib-backend';
import { LEGAL_TEMPLATE_VARIABLES } from '@/app/models/legal-documents';

const KNOWN_TOKENS = new Set(LEGAL_TEMPLATE_VARIABLES.map((v) => v.token));

/**
 * A `{{ … }}` expression is valid when it references a known variable,
 * optionally followed by the single supported `| raw` filter (which must be
 * last — mirroring the backend contract).
 */
function isValidExpression(inner: string): boolean {
    const parts = inner.split('|').map((p) => p.trim());
    if (!KNOWN_TOKENS.has(parts[0])) return false;
    return parts.length === 1 || (parts.length === 2 && parts[1] === 'raw');
}

/** Wrap every {{ … }} expression in a validity-colored span. */
function highlightLiquidTokens(html: string): string {
    return html.replace(/\{\{([^{}]*)\}\}/g, (match, inner: string) => {
        const valid = isValidExpression(inner);
        const cls = valid ? 'liquid-token-valid' : 'liquid-token-invalid';
        const title = valid
            ? 'Known template variable'
            : 'Unknown template variable';
        return `<span class="${cls}" title="${title}">${match}</span>`;
    });
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Read-only view of a legal document: the title as a label and the content
 * rendered as HTML, with an optional Edit affordance (the host opens its own
 * edit dialog). Liquid `{{ … }}` expressions are highlighted green (known
 * variable) or red (unknown / malformed). Shared by the category editor and
 * the realm Legal tab.
 */
@Component({
    selector: 'app-legal-doc-view',
    standalone: true,
    imports: [NgClass, Button],
    template: `
        <div class="flex flex-col gap-3" [class.h-full]="fitHeight()">
            <div class="flex items-start justify-between gap-3">
                @if (doc().Title) {
                    <div
                        class="legal-doc-preview font-semibold text-lg"
                        [innerHTML]="renderTitle(doc().Title)"
                    ></div>
                } @else {
                    <div class="font-semibold text-lg">—</div>
                }
                @if (editable()) {
                    <p-button
                        label="Edit"
                        icon="pi pi-pencil"
                        size="small"
                        outlined
                        (click)="edit.emit()"
                    />
                }
            </div>
            @if (doc().Content) {
                <div
                    class="legal-doc-preview rounded-lg border border-surface-200 dark:border-surface-700 p-4 overflow-auto"
                    [ngClass]="
                        fitHeight() ? 'flex-1 min-h-40' : 'max-h-[32rem]'
                    "
                    [innerHTML]="renderContent(doc().Content)"
                ></div>
            } @else {
                <div class="text-muted-color italic">No content yet.</div>
            }
        </div>
    `,
    styles: `
        /* Rendered document preview: restore the text styles Tailwind preflight
           resets. ::ng-deep is required because the markup comes via innerHTML
           and carries no emulated-encapsulation attributes. */
        :host ::ng-deep .legal-doc-preview h1,
        :host ::ng-deep .legal-doc-preview h2,
        :host ::ng-deep .legal-doc-preview h3,
        :host ::ng-deep .legal-doc-preview h4 {
            font-weight: 600;
            margin: 1rem 0 0.5rem;
        }
        :host ::ng-deep .legal-doc-preview h1 {
            font-size: 1.5rem;
        }
        :host ::ng-deep .legal-doc-preview h2 {
            font-size: 1.25rem;
        }
        :host ::ng-deep .legal-doc-preview h3 {
            font-size: 1.1rem;
        }
        :host ::ng-deep .legal-doc-preview p {
            margin: 0.5rem 0;
        }
        :host ::ng-deep .legal-doc-preview ul,
        :host ::ng-deep .legal-doc-preview ol {
            margin: 0.5rem 0;
            padding-left: 1.5rem;
        }
        :host ::ng-deep .legal-doc-preview ul {
            list-style: disc;
        }
        :host ::ng-deep .legal-doc-preview ol {
            list-style: decimal;
        }
        :host ::ng-deep .legal-doc-preview a {
            color: var(--p-primary-color);
            text-decoration: underline;
        }
        :host ::ng-deep .legal-doc-preview .liquid-token-valid,
        :host ::ng-deep .legal-doc-preview .liquid-token-invalid {
            font-family: monospace;
            font-size: 0.9em;
            border-radius: 0.25rem;
            padding: 0 0.25rem;
        }
        :host ::ng-deep .legal-doc-preview .liquid-token-valid {
            color: var(--p-green-700);
            background: color-mix(in srgb, var(--p-green-500) 15%, transparent);
        }
        :host ::ng-deep .legal-doc-preview .liquid-token-invalid {
            color: var(--p-red-700);
            background: color-mix(in srgb, var(--p-red-500) 15%, transparent);
        }
    `,
})
export class LegalDocView {
    readonly doc = input.required<ContentDocument>();
    readonly editable = input(false);
    /** Stretch to the host's height (resizable edit dialog) instead of capping at 32rem. */
    readonly fitHeight = input(false);
    readonly edit = output<void>();

    /** Title is plain text — escape it, then highlight liquid expressions. */
    protected renderTitle(title: string): string {
        return highlightLiquidTokens(escapeHtml(title));
    }

    /**
     * Content is HTML: highlight liquid expressions FIRST (on the raw markup,
     * so an &nbsp; smuggled inside {{ … }} is flagged red exactly like the
     * server-side parser would reject it), then normalize the remaining
     * &nbsp; so text wraps normally.
     */
    protected renderContent(html: string): string {
        return highlightLiquidTokens(html).replace(/&nbsp;/g, ' ');
    }
}
